import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEye, FiSearch, FiCalendar, FiCheckCircle, FiEdit } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import siteVisitService from '../services/siteVisitService';
import Button from '../components/Button';
import './SiteVisits.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const SiteVisits = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, filterStatus, filterPriority, startDate, endDate, effectiveLimit]);

  useEffect(() => {
    fetchVisits();
  }, [currentPage, appliedSearch, filterStatus, filterPriority, startDate, endDate, effectiveLimit]);

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await siteVisitService.getAll(params);
      setVisits(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch site visits');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      scheduled: 'badge-primary',
      in_progress: 'badge-warning',
      completed: 'badge-success',
      cancelled: 'badge-error',
      on_hold: 'badge-gray',
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent',
    };
    return priorityMap[priority] || 'priority-medium';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Site Visits</h2>
          <p className="page-subtitle">Manage service calls and site visits</p>
        </div>
        <Link to="/site-visits/new">
          <Button variant="primary" icon={<FiPlus />}>
            Create Visit
          </Button>
        </Link>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by customer, ticket, or complaint..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="on_hold">On Hold</option>
        </select>

        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>

        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="filter-select"
          placeholder="Start Date"
        />

        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="filter-select"
          placeholder="End Date"
        />

      </div>

      {visits.length === 0 ? (
        <div className="empty-state">
          <FiCalendar className="empty-icon" />
          <h3>No Site Visits Found</h3>
          <p>Create your first site visit</p>
          <Link to="/site-visits/new">
            <Button variant="primary" icon={<FiPlus />}>
              Create Visit
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer / Ticket</th>
                  <th>Complaint</th>
                  <th>Scheduled</th>
                  <th>Technician</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => (
                  <tr key={visit._id}>
                    <td>
                      <strong>{(visit.customer && visit.customer.name) || visit.customerName || '—'}</strong>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                        #{visit.ticketNumber}
                      </div>
                    </td>
                    <td style={{ maxWidth: 200 }}>{visit.complaint}</td>
                    <td>
                      {format(new Date(visit.scheduledDate), 'MMM dd, yyyy')}
                      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                        {format(new Date(visit.scheduledDate), 'hh:mm a')}
                      </div>
                    </td>
                    <td>{visit.assignedTechnician?.name || '—'}</td>
                    <td>{(visit.customer && visit.customer.address?.city) || '—'}</td>
                    <td>
                      <span className={getStatusBadge(visit.status)}>
                        {visit.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Link to={`/site-visits/${visit._id}`} className="action-btn action-btn-view">
                          <FiEye /> View
                        </Link>
                        {visit.status !== 'cancelled' && (
                          <Link to={`/site-visits/${visit._id}/edit`} className="action-btn action-btn-edit">
                            <FiEdit /> Edit
                          </Link>
                        )}
                        {visit.status !== 'completed' && visit.status !== 'cancelled' && (
                          <Link to={`/site-visits/${visit._id}/complete`} className="action-btn action-btn-complete">
                            <FiCheckCircle /> Complete
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > visits.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–{Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select value={limitOption} onChange={(e) => setLimitOption(e.target.value)} className="pagination-limit-select">
                  {LIMIT_OPTIONS.map((n) => (<option key={n} value={String(n)}>{n}</option>))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input type="number" min={1} max={100} value={customLimit} onChange={(e) => setCustomLimit(parseInt(e.target.value, 10) || 10)} className="pagination-limit-input" />
                )}
              </div>
              <div className="pagination-controls">
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>Page {currentPage} of {pages}</span>
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))} disabled={currentPage === pages}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SiteVisits;
